import React, { useState, useEffect } from "react";
import { Channel } from "stream-chat-react";
import { Avatar, Button, Drawer, Box, Modal, Badge } from "@mui/material";
import { styled } from "@mui/material/styles";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import { useStreamChatClient } from "../context/StreamChatContext";

/* ───────── ユーティリティ：ミュート判定 ───────── */
const isChannelMuted = (client, channel) =>
  !!client?.mutedChannels?.some((m) => (m.channel?.cid ?? m.cid) === channel?.cid);

const DisplayMemberAndStatus = ({ group, user }) => {
  const chatClient = useStreamChatClient(); // StreamChat インスタンス想定
  const [channel, setChannel] = useState(null);

  /* ───────── チャンネル初期化 ───────── */
  useEffect(() => {
    let mounted = true;
    const initChat = async () => {
      if (!chatClient || !group?.id) return;
      const ch = chatClient.channel("messaging", "groupChat-" + group.id, {});
      if (mounted) setChannel(ch);
      try {
        await ch.watch();
      } catch (e) {
        console.error("channel.watch failed:", e);
      }
    };
    initChat();
    return () => {
      mounted = false;
      // watch のクリーンアップ（必要に応じて）
      // channel?.stopWatching?.().catch(()=>{});
    };
  }, [chatClient, group?.id]);

  /* ───────── メンバー取得 ───────── */
  const [channelMember, setChannelMember] = useState([]);
  useEffect(() => {
    const getChannelMembers = async () => {
      try {
        const sort = { created_at: 1 };
        const res = await channel.queryMembers({}, sort, {});
        setChannelMember(res?.members ?? []);
      } catch (e) {
        console.error("queryMembers failed:", e);
      }
    };
    if (channel) getChannelMembers();
  }, [channel]);

  /* ───────── ミュート状態 ───────── */
  const [isMute, setIsMute] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 初期化 & イベントでの追従
  useEffect(() => {
    if (!chatClient || !channel) return;

    const refresh = () => setIsMute(isChannelMuted(chatClient, channel));
    refresh();

    const sub = chatClient.on((e) => {
      if (
        e.type === "channel.muted" ||
        e.type === "channel.unmuted" ||
        e.type === "notification.channel_mutes_updated" ||
        e.type === "notification.mutes_updated" ||
        e.type === "health.check"
      ) {
        refresh();
      }
    });

    return () => sub.unsubscribe();
  }, [chatClient, channel?.cid]);

  const handleMute = async () => {
    if (!channel) return;
    try {
      setIsLoading(true);
      if (isChannelMuted(chatClient, channel)) {
        await channel.unmute();
      } else {
        await channel.mute();
      }
      // イベントで追従するが、即時反映したいので手動更新も
      setIsMute(isChannelMuted(chatClient, channel));
    } catch (e) {
      console.error("mute/unmute failed:", e);
    } finally {
      setIsLoading(false);
    }
  };

  /* ───────── ユーザーのVillager判定 ───────── */
  const isVillager = async (userId) => {
    try {
      const snap = await getDoc(doc(db, "users", userId));
      return snap.exists() && snap.data().isVillager === true;
    } catch (error) {
      console.error("Error getting document:", error);
      return false;
    }
  };

  /* ───────── Villager付きメンバー配列 ───────── */
  const [channelMemberWithVillagerStatus, setChannelMemberWithVillagerStatus] = useState([]);
  useEffect(() => {
    const fetchVillagerStatus = async () => {
      const memberStatuses = await Promise.all(
        (channelMember ?? []).map(async (member) => {
          const flag = await isVillager(member.user.id);
          return { ...member, isVillager: flag };
        })
      );
      setChannelMemberWithVillagerStatus(memberStatuses);
    };
    if (channelMember?.length) fetchVillagerStatus();
  }, [channelMember]);

  /* ───────── UI用 ───────── */
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: "#44b700",
      color: "#44b700",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      "&::after": {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        animation: "ripple 1.2s infinite ease-in-out",
        border: "1px solid currentColor",
        content: '""',
      },
    },
    "@keyframes ripple": {
      "0%": { transform: "scale(.8)", opacity: 1 },
      "100%": { transform: "scale(2.4)", opacity: 0 },
    },
  }));

  return (
    <>
      <div className="font-bold text-lg">メンバー</div>

      {/* ミュート切替（例：どこかにボタン） */}
      <div className="my-2">
        <Button variant="outlined" onClick={handleMute} disabled={isLoading || !channel}>
          {isMute ? "ミュート解除" : "このチャンネルをミュート"}
        </Button>
      </div>

      <div>
        {channelMemberWithVillagerStatus && user?.isAdmin && (
          <>
            {channelMemberWithVillagerStatus.map(
              (member) =>
                member?.isVillager && (
                  <div
                    key={member.user.id}
                    className="flex justify-between space-x-1 items-center rounded-md border w-full my-1 py-2 px-4 shadow-md hover:cursor-pointer hover:shadow-xl duration-150 hover:bg-gray-50 group"
                  >
                    <div
                      className="flex space-x-2 items-center"
                      onClick={() => window.open("/user/" + member?.user.id, "_blank")}
                    >
                      <div className="flex flex-col justify-center items-center space-y-2 ">
                        {member?.user.online ? (
                          <StyledBadge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            variant="dot"
                          >
                            <Avatar alt={member?.user.name} src={member?.user.image} />
                          </StyledBadge>
                        ) : (
                          <Avatar src={member?.user.image} />
                        )}
                      </div>
                      <div>
                        <div className="font-bold">{member?.user.name}</div>
                        <div className="text-gray-400">
                          {new Date(member?.updated_at).toLocaleString()}参加
                        </div>
                      </div>
                      <div className="group-hover:block hidden duration-150">
                        <Button size="small" variant="outlined">
                          プロフィール
                        </Button>
                      </div>
                    </div>
                  </div>
                )
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DisplayMemberAndStatus;
